/**
 * Custom hooks for view-specific preferences
 * Provides convenient access to preferences for each view type
 */

import { useCallback } from 'react';
import { usePreferences } from '../contexts/PreferencesContext';
import type { TrackerPreferences } from './preferences';

/**
 * Hook for List view preferences
 */
export function useListPreferences(projectId: string, boardId: string) {
  const { getListPreferences, setListPreferences } = usePreferences();

  const prefs = getListPreferences(projectId, boardId);

  const setDensity = useCallback(
    (density: 'compact' | 'normal' | 'comfortable') => {
      setListPreferences(projectId, boardId, { density });
    },
    [projectId, boardId, setListPreferences]
  );

  const setVisibleColumns = useCallback(
    (columns: string[]) => {
      setListPreferences(projectId, boardId, { visibleColumns: columns });
    },
    [projectId, boardId, setListPreferences]
  );

  const setGroupBy = useCallback(
    (groupBy: 'none' | 'status' | 'type' | 'assignee' | 'priority') => {
      setListPreferences(projectId, boardId, { groupBy });
    },
    [projectId, boardId, setListPreferences]
  );

  const updatePreferences = useCallback(
    (updates: Partial<TrackerPreferences['list'][string]>) => {
      setListPreferences(projectId, boardId, updates);
    },
    [projectId, boardId, setListPreferences]
  );

  return {
    density: prefs.density,
    visibleColumns: prefs.visibleColumns,
    groupBy: prefs.groupBy,
    setDensity,
    setVisibleColumns,
    setGroupBy,
    updatePreferences,
  };
}

/**
 * Hook for Gantt view preferences
 */
export function useGanttPreferences(projectId: string, boardId: string) {
  const { getGanttPreferences, setGanttPreferences } = usePreferences();

  const prefs = getGanttPreferences(projectId, boardId);

  const setView = useCallback(
    (view: 'day' | 'week' | 'month' | 'year') => {
      setGanttPreferences(projectId, boardId, { view });
    },
    [projectId, boardId, setGanttPreferences]
  );

  const setShowCriticalPath = useCallback(
    (show: boolean) => {
      setGanttPreferences(projectId, boardId, { showCriticalPath: show });
    },
    [projectId, boardId, setGanttPreferences]
  );

  const setShowDependencies = useCallback(
    (show: boolean) => {
      setGanttPreferences(projectId, boardId, { showDependencies: show });
    },
    [projectId, boardId, setGanttPreferences]
  );

  const setShowSlackTimes = useCallback(
    (show: boolean) => {
      setGanttPreferences(projectId, boardId, { showSlackTimes: show });
    },
    [projectId, boardId, setGanttPreferences]
  );

  const setGroupByEpic = useCallback(
    (group: boolean) => {
      setGanttPreferences(projectId, boardId, { groupByEpic: group });
    },
    [projectId, boardId, setGanttPreferences]
  );

  const setResourcePanelOpen = useCallback(
    (open: boolean) => {
      setGanttPreferences(projectId, boardId, { resourcePanelOpen: open });
    },
    [projectId, boardId, setGanttPreferences]
  );

  const updatePreferences = useCallback(
    (updates: Partial<TrackerPreferences['gantt'][string]>) => {
      setGanttPreferences(projectId, boardId, updates);
    },
    [projectId, boardId, setGanttPreferences]
  );

  return {
    view: prefs.view,
    showCriticalPath: prefs.showCriticalPath,
    showDependencies: prefs.showDependencies,
    showSlackTimes: prefs.showSlackTimes,
    groupByEpic: prefs.groupByEpic,
    resourcePanelOpen: prefs.resourcePanelOpen,
    setView,
    setShowCriticalPath,
    setShowDependencies,
    setShowSlackTimes,
    setGroupByEpic,
    setResourcePanelOpen,
    updatePreferences,
  };
}

/**
 * Hook for Kanban view preferences
 */
export function useKanbanPreferences(projectId: string, boardId: string) {
  const { getKanbanPreferences, setKanbanPreferences } = usePreferences();

  const prefs = getKanbanPreferences(projectId, boardId);

  const setCollapsedColumns = useCallback(
    (columns: string[]) => {
      setKanbanPreferences(projectId, boardId, { collapsedColumns: columns });
    },
    [projectId, boardId, setKanbanPreferences]
  );

  const toggleColumnCollapse = useCallback(
    (columnId: string) => {
      const current = prefs.collapsedColumns;
      const isCollapsed = current.includes(columnId);

      const updated = isCollapsed
        ? current.filter(id => id !== columnId)
        : [...current, columnId];

      setKanbanPreferences(projectId, boardId, { collapsedColumns: updated });
    },
    [projectId, boardId, prefs.collapsedColumns, setKanbanPreferences]
  );

  const setSwimlaneMode = useCallback(
    (mode: 'none' | 'assignee' | 'priority' | 'epic') => {
      setKanbanPreferences(projectId, boardId, { swimlaneMode: mode });
    },
    [projectId, boardId, setKanbanPreferences]
  );

  const updatePreferences = useCallback(
    (updates: Partial<TrackerPreferences['kanban'][string]>) => {
      setKanbanPreferences(projectId, boardId, updates);
    },
    [projectId, boardId, setKanbanPreferences]
  );

  return {
    collapsedColumns: prefs.collapsedColumns,
    swimlaneMode: prefs.swimlaneMode,
    setCollapsedColumns,
    toggleColumnCollapse,
    setSwimlaneMode,
    updatePreferences,
  };
}

/**
 * Hook for global preferences
 */
export function useGlobalPreferences() {
  const { getGlobalPreferences, setGlobalPreferences } = usePreferences();

  const prefs = getGlobalPreferences();

  const setSidebarCollapsed = useCallback(
    (collapsed: boolean) => {
      setGlobalPreferences({ sidebarCollapsed: collapsed });
    },
    [setGlobalPreferences]
  );

  const setTheme = useCallback(
    (theme: 'light' | 'dark') => {
      setGlobalPreferences({ theme });
    },
    [setGlobalPreferences]
  );

  const updatePreferences = useCallback(
    (updates: Partial<TrackerPreferences['global']>) => {
      setGlobalPreferences(updates);
    },
    [setGlobalPreferences]
  );

  return {
    sidebarCollapsed: prefs.sidebarCollapsed,
    theme: prefs.theme,
    setSidebarCollapsed,
    setTheme,
    updatePreferences,
  };
}
